/* Loomea drawer: replaces Elixir assets/cart-drawer.js. */
(() => {
  if (customElements.get('cart-drawer')) return;

  class LoomeaCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.busy = false;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onHeader = this.onHeader.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKey);

      document.addEventListener('click', this.onHeader);
      document.addEventListener('keydown', this.onHeader);

      this.header = document.getElementById('cart-icon-bubble');

      if (this.header) {
        this.header.setAttribute('aria-haspopup', 'dialog');
        this.header.setAttribute('role', 'button');
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKey);

      document.removeEventListener('click', this.onHeader);
      document.removeEventListener('keydown', this.onHeader);

      if (this.classList.contains('active')) this.close();
    }

    onHeader(event) {
      if (
        event.type === 'keydown' &&
        !['Enter', ' '].includes(event.key)
      ) {
        return;
      }

      const trigger = event.target.closest('#cart-icon-bubble');

      if (trigger) {
        event.preventDefault();
        this.open(trigger);
      }
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    open(trigger) {
      if (trigger) this.setActiveElement(trigger);

      if (!this.classList.contains('active')) {
        this.preexistingOverflow =
          document.body.classList.contains('overflow-hidden');

        this.previousOverflow = document.body.style.overflow;
      }

      this.classList.add('active', 'animate');
      document.body.classList.add('overflow-hidden');
      document.body.style.overflow = 'hidden';

      this.querySelector('.drawer__inner')?.focus();
    }

    close() {
      if (!this.classList.contains('active')) return;

      this.classList.remove('active');

      if (!this.preexistingOverflow) {
        document.body.classList.remove('overflow-hidden');
      }

      document.body.style.overflow = this.previousOverflow || '';

      if (this.activeElement?.isConnected) {
        this.activeElement.focus();
      }
    }

    onKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (
        event.key === 'Enter' &&
        event.target.matches('[data-lm-quantity]')
      ) {
        event.preventDefault();
        this.onChange(event);
        return;
      }

      if (
        event.key !== 'Tab' ||
        !this.classList.contains('active')
      ) {
        return;
      }

      const focusable = [
        ...this.querySelectorAll(
          'button:not(:disabled),a[href],input:not(:disabled)'
        )
      ].filter(element => {
        return (
          element.getAttribute('tabindex') !== '-1' &&
          element.getClientRects().length
        );
      });

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first) {
        event.preventDefault();
        return;
      }

      if (
        event.shiftKey &&
        (
          document.activeElement === first ||
          document.activeElement ===
            this.querySelector('.drawer__inner')
        )
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer'
        },
        {
          id: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    getSectionInnerHTML(
      html,
      selector = '.shopify-section'
    ) {
      if (typeof html !== 'string') {
        throw new Error('Cart content was not returned.');
      }

      const node = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);

      if (!node) {
        throw new Error('The cart layout could not be refreshed.');
      }

      return node.innerHTML;
    }

    getSectionDOM(
      html,
      selector = '.shopify-section'
    ) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
    }

    renderContents(data, shouldOpen = true) {
      const html = data.sections?.['cart-drawer'];
      const content = this.getSectionInnerHTML(
        html,
        '#CartDrawer'
      );

      const parsed = this.getSectionDOM(
        html,
        '#CartDrawer'
      );

      const target = this.querySelector('#CartDrawer');

      if (!target) {
        throw new Error('The cart container is missing.');
      }

      // Validate before replacing anything.
      // A missing section is not an empty cart.
      target.innerHTML = content;
      target.dataset.count = parsed.dataset.count;

      this.classList.toggle(
        'is-empty',
        Number(parsed.dataset.count) === 0
      );

      const bubble = document.getElementById(
        'cart-icon-bubble'
      );

      if (
        bubble &&
        typeof data.sections?.['cart-icon-bubble'] === 'string'
      ) {
        const updated = this.getSectionDOM(
          data.sections['cart-icon-bubble']
        );

        if (updated) {
          bubble.innerHTML = updated.innerHTML;
        }
      }

      if (shouldOpen) this.open();
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));

      this.querySelectorAll(
        '[data-lm-delta],' +
        '[data-lm-remove],' +
        '[data-lm-quantity],' +
        '[data-lm-add],' +
        '#CartDrawer-Checkout'
      ).forEach(element => {
        element.disabled = busy;
      });
    }

    error(message) {
      const element = this.querySelector(
        '#CartDrawer-CartErrors'
      );

      if (element) element.textContent = message;
    }

    onClick(event) {
      if (event.target.closest('[data-lm-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const add = event.target.closest('[data-lm-add]');

      if (add) {
        event.preventDefault();

        this.mutate('add', {
          items: [
            {
              id: add.dataset.lmAdd,
              quantity: 1
            }
          ]
        });

        return;
      }

      const button = event.target.closest(
        '[data-lm-delta],[data-lm-remove]'
      );

      if (!button) return;

      event.preventDefault();

      if (this.busy) return;

      const row = button.closest('[data-lm-line]');
      const input = row.querySelector('[data-lm-quantity]');
      const current = Number(row.dataset.quantity);
      const step = Number(input.step) || 1;
      const min = Number(input.dataset.min) || 1;

      let quantity = button.hasAttribute('data-lm-remove')
        ? 0
        : current + Number(button.dataset.lmDelta) * step;

      if (quantity < min) quantity = 0;

      this.change(row, quantity);
    }

    onChange(event) {
      if (!event.target.matches('[data-lm-quantity]')) {
        return;
      }

      const row = event.target.closest('[data-lm-line]');

      if (this.busy) {
        event.target.value = row.dataset.quantity;
        return;
      }

      this.change(
        row,
        event.target.value === ''
          ? NaN
          : Number(event.target.value)
      );
    }

    change(row, quantity) {
      const input = row.querySelector('[data-lm-quantity]');
      const min = Number(input.dataset.min) || 1;
      const step = Number(input.step) || 1;

      const max = input.max === ''
        ? Infinity
        : Number(input.max);

      const invalid =
        !Number.isInteger(quantity) ||
        quantity < 0 ||
        (
          quantity !== 0 &&
          (
            quantity < min ||
            quantity > max ||
            (quantity - min) % step !== 0
          )
        );

      if (invalid) {
        input.value = row.dataset.quantity;

        this.error(
          'Please choose an available quantity' +
          (max !== Infinity ? ' up to ' + max : '') +
          '.'
        );

        return;
      }

      if (quantity === Number(row.dataset.quantity)) {
        return;
      }

      // Use the unique cart-line key, not the variant ID.
      // Different subscription plans can share a variant.
      this.mutate('change', {
        id: row.dataset.key,
        quantity
      });
    }

    async refresh() {
      const route = window.Shopify?.routes?.root || '/';

      const response = await fetch(
        route + 'cart?sections=cart-drawer,cart-icon-bubble',
        {
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Unable to refresh cart.');
      }

      this.renderContents(
        {
          sections: await response.json()
        },
        this.classList.contains('active')
      );
    }

    async mutate(action, payload) {
      if (this.busy) return;

      const route = window.Shopify?.routes?.root || '/';

      this.error('');
      this.setBusy(true);

      let confirmed = false;

      try {
        const response = await fetch(
          route + 'cart/' + action + '.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              ...payload,
              sections: [
                'cart-drawer',
                'cart-icon-bubble'
              ],
              sections_url: window.location.pathname
            })
          }
        );

        const data = await response.json();

        if (!response.ok || data.errors || data.status) {
          const message = typeof data.errors === 'string'
            ? data.errors
            : data.description || data.message;

          this.querySelectorAll(
            '[data-lm-quantity]'
          ).forEach(input => {
            input.value = input
              .closest('[data-lm-line]')
              .dataset.quantity;
          });

          this.error(
            message ||
            'This quantity is unavailable. Please choose another quantity.'
          );

          return;
        }

        confirmed = true;

        try {
          this.renderContents(
            data,
            this.classList.contains('active')
          );
        } catch {
          await this.refresh();
        }

        const status = this.querySelector('[data-lm-status]');

        if (status) {
          status.textContent = 'Cart updated.';
        }
      } catch (error) {
        // Read fresh state after an uncertain failure.
        // Never repeat the add/change request automatically.
        try {
          await this.refresh();

          this.error(
            confirmed
              ? ''
              : 'Your cart has been refreshed. Check the quantity before trying again.'
          );
        } catch {
          this.error(
            confirmed
              ? 'Your change was saved, but the cart could not refresh. Reload the page to see it.'
              : 'We could not confirm the change. Reload the page to check your cart before trying again.'
          );
        }
      } finally {
        this.setBusy(false);
      }
    }
  }

  customElements.define(
    'cart-drawer',
    LoomeaCartDrawer
  );
})();